import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MetaHead from './MetaHead';
import Navbar from './Navbar';
import { getBlogPostByUrlFriendlyName } from '../data/blogs';
import { BlogContentBlock } from '../types/blog';
import { trackPageVisit } from '../utils/analytics';

const BlogPost: React.FC = () => {
  const { blogurlfriendlyname } = useParams<{ blogurlfriendlyname: string }>();
  const { t } = useTranslation();

  useEffect(() => {
    trackPageVisit(`blog-${blogurlfriendlyname}`);
  }, [blogurlfriendlyname]);

  const blogPost = blogurlfriendlyname ? getBlogPostByUrlFriendlyName(blogurlfriendlyname) : undefined;

  if (!blogPost) {
    return (
      <>
        <MetaHead
          title="Blog Post Not Found - Streak Spark"
          description="The blog post you're looking for doesn't exist."
          keywords="blog, streak spark, not found"
        />
        <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 dark:from-neutral-900 dark:via-neutral-800 dark:to-neutral-900 p-4">
          <Navbar pageTitle="Blog" />
          <div className="max-w-4xl mx-auto">
            <div className="glass-card p-8 rounded-2xl text-center">
              <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-4">
                {t('blog.blogPostNotFound')}
              </h1>
              <p className="text-neutral-600 dark:text-neutral-400 mb-6">
                {t('blog.blogPostNotFoundMessage')}
              </p>
              <Link
                to="/"
                className="inline-flex items-center px-6 py-3 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 transition-colors duration-300"
              >
                {t('blog.backToHome')}
              </Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  const renderContentBlock = (block: BlogContentBlock, index: number) => {
    switch (block.type) {
      case 'paragraph':
        return (
          <p key={index} className="text-neutral-700 dark:text-neutral-300 leading-relaxed mb-6">
            {block.content}
          </p>
        );

      case 'heading':
        const level = block.level || 2;
        const headingClass = `font-bold text-neutral-900 dark:text-neutral-100 mb-4 mt-8 ${
          level === 1 ? 'text-3xl' :
          level === 2 ? 'text-2xl' :
          level === 3 ? 'text-xl' :
          'text-lg'
        }`;

        if (level === 1) {
          return <h1 key={index} className={headingClass}>{block.content}</h1>;
        } else if (level === 2) {
          return <h2 key={index} className={headingClass}>{block.content}</h2>;
        } else if (level === 3) {
          return <h3 key={index} className={headingClass}>{block.content}</h3>;
        } else if (level === 4) {
          return <h4 key={index} className={headingClass}>{block.content}</h4>;
        } else if (level === 5) {
          return <h5 key={index} className={headingClass}>{block.content}</h5>;
        } else {
          return <h6 key={index} className={headingClass}>{block.content}</h6>;
        }

      case 'image':
        return (
          <div key={index} className="my-8">
            <img
              src={block.src}
              alt={block.alt || ''}
              className="w-full max-w-4xl mx-auto rounded-xl shadow-lg"
            />
            {block.alt && (
              <p className="text-sm text-neutral-500 dark:text-neutral-400 text-center mt-2 italic">
                {block.alt}
              </p>
            )}
          </div>
        );

      case 'video':
        return (
          <div key={index} className="my-8">
            <div className="aspect-video max-w-4xl mx-auto">
              <iframe
                src={block.url}
                title={block.alt || 'Video'}
                className="w-full h-full rounded-xl shadow-lg"
                allowFullScreen
              />
            </div>
            {block.alt && (
              <p className="text-sm text-neutral-500 dark:text-neutral-400 text-center mt-2 italic">
                {block.alt}
              </p>
            )}
          </div>
        );

      case 'link':
        return (
          <div key={index} className="my-6">
            <a
              href={block.href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 rounded-lg hover:bg-primary-200 dark:hover:bg-primary-800 transition-colors duration-300"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              {block.text}
            </a>
          </div>
        );

      case 'list':
        return (
          <ul key={index} className="list-disc list-inside text-neutral-700 dark:text-neutral-300 leading-relaxed mb-6 space-y-2">
            {block.items?.map((item, itemIndex) => (
              <li key={itemIndex}>{item}</li>
            ))}
          </ul>
        );

      default:
        return null;
    }
  };

  const formattedDate = new Date(blogPost.publishedDate).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <>
      <MetaHead
        title={`${blogPost.title} - Streak Spark Blog`}
        description={blogPost.excerpt}
        keywords={blogPost.tags?.join(', ') || 'blog, habits, productivity'}
      />

      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 dark:from-neutral-900 dark:via-neutral-800 dark:to-neutral-900 p-4">
        <Navbar pageTitle="Blog" />

        <div className="max-w-4xl mx-auto">
          {/* Article Header */}
          <div className="glass-card p-8 rounded-2xl mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-neutral-900 dark:text-neutral-100 mb-4 leading-tight">
              {blogPost.title}
            </h1>

            {blogPost.featuredImage && (
              <img
                src={blogPost.featuredImage}
                alt={blogPost.title}
                className="w-full h-64 md:h-80 object-cover rounded-xl shadow-lg mb-8"
              />
            )}

            <div className="flex flex-wrap items-center text-sm text-neutral-600 dark:text-neutral-400 mb-6">
              <span className="mr-4">{t('blog.by')} {blogPost.author}</span>
              <span className="mr-4">{t('blog.published')} {formattedDate}</span>
              {blogPost.tags && blogPost.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {blogPost.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-neutral-100 dark:bg-neutral-800 rounded-full text-xs"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <p className="text-lg text-neutral-700 dark:text-neutral-300 leading-relaxed">
              {blogPost.excerpt}
            </p>
          </div>

          {/* Article Content */}
          <div className="glass-card p-8 rounded-2xl mb-8">
            <article className="prose prose-lg dark:prose-invert max-w-none">
              {blogPost.content.map((block, index) => renderContentBlock(block, index))}
            </article>
          </div>

          {/* Article Footer */}
          <div className="glass-card p-8 rounded-2xl">
            <div className="flex justify-between items-center">
              <Link
                to="/"
                className="inline-flex items-center px-6 py-3 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 transition-colors duration-300"
              >
                {t('blog.backToHome')}
              </Link>

              <div className="text-sm text-neutral-500 dark:text-neutral-400">
                {t('blog.lastUpdated')}: {blogPost.lastModified ? new Date(blogPost.lastModified).toLocaleDateString('en-US') : formattedDate}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BlogPost;
